import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { analysisApi } from '../api/AnalysisApi';
import { DEFAULT_BASKET_MONITOR_CONFIG, basketSeverityRank, computeBasketAccountView, computeBasketTakeProfits, computeBasketViews, } from '../utils/basketMonitor';
import { useUserAccounts } from './UserAccountsContext';
import { useAccountData } from './AccountDataContext';
import { computePairOpenCosts } from '../utils/pairCosts';
const BasketMonitorContext = createContext(undefined);
export const useBasketMonitor = () => {
    const context = useContext(BasketMonitorContext);
    if (!context) {
        throw new Error('useBasketMonitor must be used within BasketMonitorProvider');
    }
    return context;
};
/**
 * Synced rows are an event log: floating profit is never updated after the
 * open event, so `floatingProfit` here is mostly just costs. The compute
 * layer re-anchors money on the live feed / value-factor estimates.
 */
const toBasketInput = (position) => ({
    symbol: position.pair,
    side: position.type === 0 ? 'buy' : 'sell',
    lots: position.lots,
    openPrice: position.openPrice,
    openTime: position.openTime,
    syncedNetUsd: position.floatingProfit,
    valueFactor: position.valueFactor,
});
export const BasketMonitorProvider = ({ children, config, onAlertUpgrade }) => {
    const monitorConfig = config ?? DEFAULT_BASKET_MONITOR_CONFIG;
    const [openPositions, setOpenPositions] = useState([]);
    const [detailLoaded, setDetailLoaded] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState(undefined);
    const { selectedAccountId } = useUserAccounts();
    const { accountData } = useAccountData();
    // Highest severity rank already notified, per basket key.
    const notifiedRanks = useRef(new Map());
    const activeRequest = useRef(0);
    const loadOrderDetail = useCallback(async () => {
        if (!selectedAccountId)
            return;
        const requestId = ++activeRequest.current;
        setDetailLoading(true);
        setDetailError(undefined);
        try {
            const inputs = await analysisApi.getDrawdownInputs(selectedAccountId);
            if (requestId !== activeRequest.current)
                return;
            setOpenPositions(inputs.openOrders);
            setDetailLoaded(true);
        }
        catch (err) {
            if (requestId !== activeRequest.current)
                return;
            console.error('Error loading basket order detail:', err);
            setDetailError(err.message || 'Failed to load order detail');
        }
        finally {
            if (requestId === activeRequest.current)
                setDetailLoading(false);
        }
    }, [selectedAccountId]);
    // Alert history and synced rows belong to the account they were loaded for.
    useEffect(() => {
        notifiedRanks.current.clear();
        setOpenPositions([]);
        setDetailLoaded(false);
    }, [selectedAccountId]);
    // Re-pull order detail whenever the account feed delivers a fresh snapshot.
    const lastUpdatedAtMs = accountData?.lastUpdatedAt?.getTime();
    useEffect(() => {
        loadOrderDetail();
    }, [loadOrderDetail, lastUpdatedAtMs]);
    const baskets = useMemo(() => {
        const inputs = openPositions
            .filter((position) => position.type === 0 || position.type === 1)
            .map(toBasketInput);
        const pairFeeds = (accountData?.pairs ?? []).map((pair) => ({
            symbol: pair.symbol,
            currentPrice: typeof pair.currentPrice === 'number' && pair.currentPrice > 0 ? pair.currentPrice : null,
            profitUsd: typeof pair.profit === 'number' ? pair.profit : null,
            openOrders: typeof pair.openOrders === 'number' ? pair.openOrders : null,
        }));
        return computeBasketViews(inputs, pairFeeds, monitorConfig, Date.now(), !detailLoaded);
    }, [openPositions, accountData, monitorConfig, detailLoaded]);
    const pairCosts = useMemo(() => computePairOpenCosts(openPositions), [openPositions]);
    const basketTakeProfits = useMemo(() => computeBasketTakeProfits(baskets), [baskets]);
    const account = useMemo(() => {
        if (!accountData)
            return undefined;
        return computeBasketAccountView(accountData.equity, accountData.totalProfit, monitorConfig);
    }, [accountData, monitorConfig]);
    useEffect(() => {
        for (const basket of baskets) {
            const rank = basketSeverityRank(basket.severity);
            const previous = notifiedRanks.current.get(basket.key) ?? 0;
            if (rank <= previous)
                continue;
            notifiedRanks.current.set(basket.key, rank);
            if (basket.severity !== 'none') {
                onAlertUpgrade?.(basket);
            }
        }
    }, [baskets, onAlertUpgrade]);
    const value = {
        baskets,
        account,
        pairCosts,
        basketTakeProfits,
        detailLoading,
        detailError,
        refresh: loadOrderDetail,
    };
    return (_jsx(BasketMonitorContext.Provider, { value: value, children: children }));
};
